import {
  type DrillAnalyticsData,
  type DrillAnalyticsSource,
  type DrillCategory,
  type DrillCompetency,
  type DrillCompetencySource,
  type DrillDefaultReport,
  type DrillFindings,
  type DrillImportResult,
  type DrillLibrary,
  type DrillLibrarySource,
  type DrillPlanner,
  type DrillPlanSource,
  type DrillRecordDetail,
  type DrillRecordSource,
  type DrillRecords,
  type DrillTemplateDetail,
  type DrillVariant,
  downloadFile,
  type ReportBrand,
  type TrainingNeedSource,
  type TrainingNeeds,
} from '@felix/ui';
import { ApiError, api, officeApi } from './api.js';

const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** PDF başlığındaki şirket markası (ADR 0057 §7); okunamazsa yok. */
export async function officeBrand(): Promise<ReportBrand | null> {
  try {
    const { branding } = await officeApi.branding();
    return branding?.name ? { name: branding.name, logo: branding.logo ?? null } : null;
  } catch {
    return null;
  }
}
const BASE = '/office/drills/library';

/** Ham `.xlsx` gövdesiyle içe aktarma (ADR 0128 §7): en çok 10 MB, kalıcı depoya yazılmaz. */
async function importFile(file: Blob, preview: boolean): Promise<DrillImportResult> {
  let response: Response;
  try {
    response = await fetch(`/api${BASE}/import?preview=${preview}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': XLSX },
      body: file,
    });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  const data = (await response.json().catch(() => ({}))) as { error?: string; detail?: unknown };
  if (!response.ok) {
    throw new ApiError(
      data.error ?? 'INTERNAL',
      response.status,
      typeof data.detail === 'string' ? data.detail : undefined,
      data,
    );
  }
  return data as DrillImportResult;
}

async function exportFile(): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`/api${BASE}/export`, { credentials: 'same-origin' });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(data.error ?? 'INTERNAL', response.status);
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'Drill_Library.xlsx';
  downloadFile(fileName, new Uint8Array(await response.arrayBuffer()), XLSX);
}

/**
 * Tatbikat Master Library (ADR 0128 Faz 1): şirket verisi, ofiste yazılır; `drill_library`
 * yeteneğini bildiren gemiye iner.
 */
export const officeDrillLibrarySource: DrillLibrarySource = {
  scope: 'office',
  load: () => api<DrillLibrary>(BASE),
  template: (id) => api<DrillTemplateDetail>(`${BASE}/templates/${encodeURIComponent(id)}`),
  saveCategory: ({ id, rowVersion, category }) =>
    id
      ? api<DrillCategory>(`${BASE}/categories/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: { rowVersion, category },
        })
      : api<DrillCategory>(`${BASE}/categories`, { method: 'POST', body: { category } }),
  saveTemplate: ({ id, rowVersion, template }) =>
    id
      ? api<DrillTemplateDetail>(`${BASE}/templates/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: { rowVersion, template },
        })
      : api<DrillTemplateDetail>(`${BASE}/templates`, { method: 'POST', body: { template } }),
  importFile,
  exportFile,
  loadDefaults: () => api<DrillDefaultReport>(`${BASE}/defaults`, { method: 'POST', body: {} }),
};

/**
 * Tatbikat planı (ADR 0128 Faz 2 Ek): ofiste filo görünümü; plan, uygulama ve erteleme gemide
 * yazılır. Ofis gemiye özel senaryoya karar verir ve şirket kural setini yazar.
 */
export const officeDrillPlanSource: DrillPlanSource = {
  scope: 'office',
  load: (vesselId) =>
    api<DrillPlanner>(
      vesselId
        ? `/office/drills/planner?vesselId=${encodeURIComponent(vesselId)}`
        : '/office/drills/planner',
    ),
  decideVariant: ({ id, decision, password }) =>
    api<DrillVariant>(`/office/drills/variants/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      body: { decision, ...(password ? { password } : {}) },
    }),
  saveRule: ({ id, rowVersion, rule }) =>
    api<unknown>('/office/drills/rules', {
      method: 'POST',
      body: { ...(id ? { id, rowVersion } : {}), rule },
    }),
  findings: (vesselId) => api<DrillFindings>(withVessel('/office/drills/findings', vesselId)),
};

const withVessel = (path: string, vesselId?: string | null) =>
  vesselId ? `${path}?vesselId=${encodeURIComponent(vesselId)}` : path;

/**
 * Tatbikat icra kayıtları (Execution Wizard; ADR 0128 Faz 3 Ek): gemi verisi pakette gelir; ofis
 * yalnız görür. Adım kanıtı ofiste açılır.
 */
export const officeDrillRecordSource: DrillRecordSource = {
  scope: 'office',
  list: (vesselId) =>
    api<DrillRecords>(
      vesselId
        ? `/office/drills/records?vesselId=${encodeURIComponent(vesselId)}`
        : '/office/drills/records',
    ),
  detail: (id) => api<DrillRecordDetail>(`/office/drills/records/${encodeURIComponent(id)}`),
  attachments: (stepId) => officeApi.pmsAttachments(stepId, 'drill_record_step'),
  attachmentUrl: (id) => `/api/office/attachments/${encodeURIComponent(id)}`,
  // Faz 4 Ek: ofisin bulgu değerlendirmesi (DPA ya da Superintendent)
  reviewFinding: ({ id, review }) =>
    api<unknown>(`/office/drills/findings/${encodeURIComponent(id)}/review`, {
      method: 'POST',
      body: { review },
    }),
  // Faz 5 Ek: Part A–E PDF'i, toplu PDF ve ZIP (tarayıcı indirmesi)
  brand: officeBrand,
  saveFile: async (fileName, bytes, type) => downloadFile(fileName, bytes, type),
};

/** Filo analitiği (Faz 5 Ek): yalnız ofis; dönem, gemi ve tür süzgeci. */
export const officeDrillAnalyticsSource: DrillAnalyticsSource = {
  load: (query) => {
    const params = new URLSearchParams();
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);
    if (query.vesselId) params.set('vesselId', query.vesselId);
    if (query.categoryId) params.set('categoryId', query.categoryId);
    const text = params.toString();
    return api<DrillAnalyticsData>(`/office/drills/analytics${text ? `?${text}` : ''}`);
  },
  saveFile: async (fileName, bytes, type) => downloadFile(fileName, bytes, type),
};

/** Crew Competency Matrix (Faz 4 Ek): gemi verisi; ofis görür. */
export const officeDrillCompetencySource: DrillCompetencySource = {
  scope: 'office',
  load: (vesselId) => api<DrillCompetency>(withVessel('/office/drills/competency', vesselId)),
  saveFile: async (fileName, bytes, type) => downloadFile(fileName, bytes, type),
};

/** Crew ▸ Training (Faz 4 Ek): eğitim ihtiyaçları gemide yazılır; ofis görür. */
export const officeTrainingNeedSource: TrainingNeedSource = {
  scope: 'office',
  load: (vesselId) => api<TrainingNeeds>(withVessel('/office/crew/training-needs', vesselId)),
};
